import { Input } from '@shared/components/ui/input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarcode, faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { LocatingProductBlock } from './LocatingProductBlock';

interface ProductToLocate {
  id: string;
  name: string;
  code: string;
  quantity: number;
  totalQuantity: number;
  location?: string;
  image?: string;
}

interface ScanLocationSectionProps {
  onLocationScan: (locationCode: string) => void;
  onScanUnits?: (productCode: string) => void;
  onClearLocation?: () => void;
  currentLocation?: {
    id: number;
    code: string;
  } | null;
  isValidating?: boolean;
  productBeingLocated?: ProductToLocate | null;
  quantityToLocate?: number;
  onQuantityChange?: (quantity: number) => void;
  onConfirmLocate?: () => void;
  isLocating?: boolean;
}

export const ScanLocationSection = ({
  onLocationScan,
  onScanUnits,
  onClearLocation,
  currentLocation,
  isValidating = false,
  productBeingLocated = null,
  quantityToLocate = 1,
  onQuantityChange = () => {},
  onConfirmLocate = () => {},
  isLocating = false,
}: ScanLocationSectionProps) => {
  const { t } = useTranslation('receiveModule');

  const handleScan = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const value = e.currentTarget.value.trim();
      if (value) {
        // Si hay un producto siendo ubicado, escanear unidades
        if (productBeingLocated && onScanUnits) {
          onScanUnits(value);
        } else {
          // Si no, escanear ubicación
          onLocationScan(value);
        }
        e.currentTarget.value = '';
      }
    }
  };

  return (
    <div className="w-full mb-4">
      {/* Scan Row - Fila con badge de ubicación + input */}
      <div className="flex items-center gap-3">
        {/* Badge de ubicación escaneada */}
        {!isValidating && currentLocation && (
          <button
            onClick={onClearLocation}
            className="inline-flex items-center gap-3 bg-card-border light:bg-badge-bg-status-3 text-badge-status-3 rounded-full px-4 py-1.5 text-base hover:bg-hover-press transition-colors cursor-pointer"
            data-testid="current-location-badge"
            title={t('ui.clear_location_tooltip')}
          >
            <FontAwesomeIcon icon={faLocationDot} className="w-4 h-4" />
            <span className="font-medium tracking-wide">{currentLocation.code}</span>
          </button>
        )}

        {/* Campo de escaneo de ubicación */}
        <div className="relative max-w-xl flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-icons">
            <FontAwesomeIcon icon={faBarcode} className="w-3.5 h-3.5" />
          </span>
          <Input
            placeholder={
              productBeingLocated
                ? t('ui.scan_units_placeholder')
                : currentLocation
                  ? t('ui.scan_product_placeholder')
                  : t('ui.scan_location_placeholder')
            }
            onKeyDown={handleScan}
            className="w-full pl-9"
            data-testid="location-scan-input"
            disabled={isValidating}
          />
        </div>
      </div>

      {/* Bloque de ubicación de producto */}
      {productBeingLocated && currentLocation && (
        <div className="mt-4">
          <LocatingProductBlock
            product={productBeingLocated}
            currentLocation={currentLocation}
            quantityToLocate={quantityToLocate}
            onQuantityChange={onQuantityChange}
            onConfirm={onConfirmLocate}
            isLocating={isLocating}
          />
        </div>
      )}

      {/* Helper text - Texto de ayuda */}
      {!productBeingLocated && (
        <div className="mt-6 text-complementary-text text-sm text-center leading-snug">
          {isValidating ? (
            <p>{t('scan.processing')}</p>
          ) : currentLocation ? (
            <p>{t('ui.scan_product_helper')}</p>
          ) : (
            <>
              <p>{t('ui.scan_instructions.line1')}</p>
              <p>{t('ui.scan_instructions.line2')}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
